import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactNode } from 'react'

import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  InlineAlert,
  Input,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@aeris/ui'

import {
  ApiError,
  fetchChannelPairing,
  startChannelPairing,
  type ChannelAccountSummaryType,
  type ChannelPairingStateType,
} from '../lib/api.ts'

/**
 * Conectar o celular da loja a uma conta de aparelho vinculado (F192).
 *
 * Os dois jeitos que o WhatsApp oferece em "Aparelhos conectados": ler um QR
 * ou digitar um código de oito letras no celular. O QR **gira** a cada poucos
 * segundos — a tela pergunta de novo de 3 em 3 s e troca a imagem, em vez de
 * deixar a pessoa apontar a câmera para um código que já venceu.
 *
 * `passkey` é o pareamento novo por chave de acesso, que o Aeris não conduz:
 * a tela manda para o painel do servidor, em vez de ficar esperando para
 * sempre.
 */

const POLL_MS = 3_000

type Method = 'qr' | 'phone'

const describeError = (cause: unknown, fallback: string): string =>
  cause instanceof ApiError ? cause.message : fallback

export const ChannelPairingDialog = ({
  account,
  onClose,
}: {
  account: ChannelAccountSummaryType
  onClose: () => void
}): ReactNode => {
  const queryClient = useQueryClient()
  const [method, setMethod] = useState<Method>('qr')
  const [phone, setPhone] = useState('')
  /** O estado devolvido pelo último `POST`; o polling assume dali em diante. */
  const [started, setStarted] = useState<ChannelPairingStateType | null>(null)
  const [error, setError] = useState<string | null>(null)

  const refreshAccounts = async (): Promise<void> => {
    await queryClient.invalidateQueries({
      queryKey: ['communication-channel-accounts'],
    })
  }

  const start = useMutation({
    mutationFn: async (input: { method: Method; phone?: string }) =>
      await startChannelPairing(account.id, input),
    onSuccess: async (state) => {
      setError(null)
      setStarted(state)
      if (state.state === 'paired') await refreshAccounts()
    },
    onError: (cause: unknown) => {
      setError(describeError(cause, 'Não foi possível iniciar a conexão.'))
    },
  })

  const polling = started !== null && started.state !== 'paired'

  const poll = useQuery({
    queryKey: ['communication-channel-pairing', account.id],
    queryFn: async ({ signal }) => {
      const state = await fetchChannelPairing(account.id, signal)
      if (state.state === 'paired') await refreshAccounts()
      return state
    },
    enabled: polling,
    refetchInterval: (query) =>
      query.state.data?.state === 'paired' ||
      query.state.data?.state === 'passkey'
        ? false
        : POLL_MS,
    // Um QR guardado de outra abertura do diálogo é um QR vencido.
    gcTime: 0,
  })

  /*
   * O polling só vale depois do início desta abertura — um `data` antigo
   * nunca chega aqui, porque `gcTime: 0` o descarta ao fechar.
   */
  const current = poll.data ?? started
  const paired = current?.state === 'paired'

  const choose = (next: Method): void => {
    setMethod(next)
    setStarted(null)
    setError(null)
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={`Conectar ${account.displayName}`}
        description="No celular da loja: WhatsApp → Aparelhos conectados → Conectar um aparelho."
        className="max-w-md"
      >
        {paired ? (
          <InlineAlert tone="success">
            Conectado. As mensagens deste número passam a chegar no Atendimento.
          </InlineAlert>
        ) : (
          <Tabs
            value={method}
            onValueChange={(value) => {
              choose(value === 'phone' ? 'phone' : 'qr')
            }}
          >
            <TabsList>
              <TabsTrigger value="qr">Ler o QR</TabsTrigger>
              <TabsTrigger value="phone">Código pelo número</TabsTrigger>
            </TabsList>

            <TabsContent value="qr" className="flex flex-col gap-3 pt-3">
              {current === null ? (
                <p className="text-(length:--text-sm) text-(--text-secondary)">
                  Gere o QR e aponte a câmera do celular para ele. Ele se renova
                  sozinho enquanto esta janela estiver aberta.
                </p>
              ) : current.state === 'qr' && current.qrCode !== null ? (
                <img
                  src={current.qrCode}
                  alt="QR para conectar o WhatsApp da loja"
                  className="mx-auto h-64 w-64 rounded-(--radius-md) bg-white p-2"
                />
              ) : current.state === 'passkey' ? null : (
                <div className="flex items-center justify-center gap-2 py-8 text-(length:--text-sm) text-(--text-secondary)">
                  <Spinner /> Esperando o WhatsApp gerar o QR…
                </div>
              )}
              {current === null ? (
                <Button
                  variant="primary"
                  loading={start.isPending}
                  onClick={() => {
                    start.mutate({ method: 'qr' })
                  }}
                >
                  Gerar QR
                </Button>
              ) : null}
            </TabsContent>

            <TabsContent value="phone" className="flex flex-col gap-3 pt-3">
              {current?.state === 'code' && current.linkingCode !== null ? (
                <>
                  <p className="text-(length:--text-sm) text-(--text-secondary)">
                    No celular, toque em “Conectar com número de telefone” e
                    digite:
                  </p>
                  <p
                    className="text-center font-(--font-mono) text-(length:--text-2xl) font-(--weight-semibold) tracking-widest text-(--text-primary) select-all"
                    aria-live="polite"
                  >
                    {current.linkingCode}
                  </p>
                </>
              ) : (
                <form
                  noValidate
                  className="flex flex-col gap-3"
                  onSubmit={(event) => {
                    event.preventDefault()
                    start.mutate({ method: 'phone', phone })
                  }}
                >
                  <Field
                    label="Número do celular da loja"
                    hint="Com DDI e DDD, como 55 31 97777-0000."
                  >
                    {(field) => (
                      <Input
                        {...field}
                        inputMode="tel"
                        autoComplete="off"
                        value={phone}
                        onChange={(event) => {
                          setPhone(event.target.value)
                        }}
                      />
                    )}
                  </Field>
                  <Button
                    type="submit"
                    variant="primary"
                    loading={start.isPending}
                    disabled={phone.replace(/\D/g, '').length < 10}
                  >
                    Gerar código
                  </Button>
                </form>
              )}
            </TabsContent>
          </Tabs>
        )}

        {current?.state === 'passkey' ? (
          <InlineAlert tone="warning">
            O celular pediu para conectar com chave de acesso, que o Aeris ainda
            não conduz. Conecte pelo painel do servidor de WhatsApp — a conta
            aqui se atualiza sozinha quando a conexão chegar.
          </InlineAlert>
        ) : null}

        {error === null ? null : (
          <InlineAlert tone="danger">{error}</InlineAlert>
        )}
        {poll.isError ? (
          <InlineAlert tone="warning">
            {describeError(
              poll.error,
              'Perdemos o contato com o servidor. Tentando de novo…'
            )}
          </InlineAlert>
        ) : null}

        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {paired ? 'Concluir' : 'Fechar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
